import type { VoidComponent } from "solid-js";
import { Show } from "solid-js";
import { useTranslation } from "../../../state/i18n";
import { useModalManager } from "../../../components/modal/ModalManager";
import InputModal from "../../../components/modal/InputModal";
import { MultiSelectWithSearchInput } from "../../../components/menu/MultiSelectWithSearch";
import UserService from "../../../services/UserService";
import { useCustomerState } from "../../../state/customer";
import { array, minLength, object, pipe } from "valibot";
import { keyFromSelector } from "i18next";
import GenericFailureModal from "../../GenericFailureModal";
import { groupReference } from "../../../utils/validation/idReferences";
import { errorTranslation } from "../../../utils/validation/base";
import { groupAvatar } from "../../../utils/avatarUtil";
import type { User } from "../../../state/user";
import { differenceWith } from "lodash-es";
import type { Group } from "../../../state/group";
import type GroupId from "../../../state/GroupId";
import type { Invite } from "../../../state/invite.ts";

type AddUserToGroupsModalProps = {
  user: User | Invite;
};

export const addUserToGroupsSchema = () =>
  object({
    groups: pipe(
      array(groupReference()),
      minLength(1, errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY))),
    ),
  });

export const AddUserToGroupsModal: VoidComponent<AddUserToGroupsModalProps> = (props) => {
  const { t } = useTranslation();
  const { replace } = useModalManager();
  const customer = useCustomerState();
  const groups = UserService.getGroups(customer.customerId);
  const addUserToGroupsMutation = UserService.addUserToGroups(customer.customerId);

  return (
    <InputModal
      variant="default"
      size="large"
      title={t(($) => $.ADD_USER_TO_GROUPS_MODAL_TITLE)}
      titleIcon="userGroup"
      cancelLabel={t(($) => $.CANCEL_BUTTON)}
      confirmLabel={t(($) => $.ADD_USER_TO_GROUPS_MODAL_ACTIONS_CONFIRM)}
      onConfirm={({ groups }) =>
        new Promise((resolve, reject) =>
          addUserToGroupsMutation.mutate(
            { id: props.user.id, groups },
            {
              onSuccess: resolve,
              onError: (err) => {
                replace({
                  content: () => (
                    <GenericFailureModal title={t(($) => $.ADD_USER_TO_GROUPS_FAILURE_MODAL_TITLE)} error={err} />
                  ),
                });
                reject(err);
              },
            },
          ),
        )
      }
      initialValue={{ groups: [] }}
      schema={addUserToGroupsSchema()}
    >
      {({ value, updateValue, error }) => (
        <Show when={groups.data}>
          {(groups) => {
            const selectableGroups = differenceWith<Group, GroupId>(groups(), props.user.groups, (a, b) =>
              a.id.equals(b),
            );

            return (
              <MultiSelectWithSearchInput
                label={t(($) => $.ADD_USER_TO_GROUPS_MODAL_CHOOSE_GROUPS)}
                placeholder={t(($) => $.ADD_USER_TO_GROUPS_MODAL_CHOOSE_GROUPS_PLACEHOLDER)}
                value={value("groups")}
                onSelect={(value) => updateValue("groups", value)}
                width="matchTrigger"
                required
                options={{
                  groups: selectableGroups.map((group) => ({
                    variant: "avatar",
                    ...groupAvatar(group),
                    label: group.name,
                    value: group.id,
                  })),
                }}
                errorMessage={error("groups")}
              />
            );
          }}
        </Show>
      )}
    </InputModal>
  );
};
